import type { RuleType, CounterPeriod } from '@duke-hq/billing/types/rules';

/**
 * Validates a money string: non-negative, max 2 decimal places
 * Examples: "0", "10", "10.5", "10.55"
 */
export function isMoney(s: string): boolean {
  return /^\d+(?:\.\d{1,2})?$/.test(s);
}

/**
 * Validates a numeric string (can be negative, any decimal places)
 * Examples: "-10", "0", "10", "10.5", "10.555"
 */
export function isNumber(s: string): boolean {
  return /^-?\d+(?:\.\d+)?$/.test(s);
}

export function isFirstN(type: RuleType): boolean {
  return (
    type === 'FIRST_N_PRICE' ||
    type === 'FIRST_N_DISCOUNT_PERCENT' ||
    type === 'FIRST_N_DISCOUNT_FIXED'
  );
}

export function isCounterPeriod(value: unknown): value is CounterPeriod {
  return value === 'LIFETIME' || value === 'YEAR' || value === 'MONTH' || value === 'WEEK';
}

export function formatDate(d?: string | Date | null): string {
  if (d == null) {
    return '—';
  }
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleDateString();
}

export function truncateString(str: string, maxLength = 30): string {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength) + '...';
}

/**
 * Normalizes a money string to have exactly 2 decimal places
 * @param value - String value to normalize
 * @returns Normalized string or original if invalid
 */
export function normalizeMoney(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || !isMoney(trimmed)) {
    return value;
  }
  const asNum = Number(trimmed);
  return asNum.toFixed(2);
}

/**
 * Determines which fields are required for a given rule type
 */
export function getRuleRequirements(type: RuleType, counterPeriod: CounterPeriod | null) {
  return {
    dayOfMonth: type === 'INVOICE_DAY_OF_MONTH',
    netDays: type === 'DUE_TERMS_NET_DAYS',
    priceOverride: type === 'PRICE_OVERRIDE' || type === 'FIRST_N_PRICE',
    discountPercent: type === 'DISCOUNT_PERCENT' || type === 'FIRST_N_DISCOUNT_PERCENT',
    discountFixed: type === 'DISCOUNT_FIXED' || type === 'FIRST_N_DISCOUNT_FIXED',
    firstN: isFirstN(type),
    counterPeriod: isCounterPeriod(counterPeriod),
  };
}

/**
 * Generates hint text for conditional fields based on rule type
 */
export function getRuleHints(type: RuleType): string {
  const hints: string[] = [];

  const req = getRuleRequirements(type, null);

  if (req.priceOverride) {
    hints.push('Provide a price like 99.00');
  }
  if (req.discountPercent) {
    hints.push('Percent like 15 or 15.5');
  }
  if (req.discountFixed) {
    hints.push('Amount like 20.00');
  }
  if (req.dayOfMonth) {
    hints.push('Day of month 1–31; clamped to month length');
  }
  if (req.netDays) {
    hints.push('Number of days after invoice date');
  }
  if (req.firstN) {
    hints.push('Set N and how the counter resets');
  }

  return hints.join(' • ');
}

export interface RuleParamPart {
  label: string;
  value: string;
}

/**
 * Gets the display parameters for a rule based on its type
 * Returns an array of label-value pairs to be displayed
 */
export function getRuleParams(rule: {
  type: RuleType;
  dayOfMonth?: number | null;
  netDays?: number | null;
  priceOverride?: string | null;
  discountPercent?: string | null;
  discountFixed?: string | null;
  firstN?: number | null;
}): RuleParamPart[] {
  const params: RuleParamPart[] = [];

  switch (rule.type) {
    case 'INVOICE_DAY_OF_MONTH':
      params.push({ label: 'Day', value: rule.dayOfMonth?.toString() ?? '—' });
      break;
    case 'DUE_TERMS_NET_DAYS':
      params.push({ label: 'NET', value: rule.netDays?.toString() ?? '0' });
      break;
    case 'PRICE_OVERRIDE':
      params.push({ label: 'Price', value: rule.priceOverride ?? '—' });
      break;
    case 'DISCOUNT_PERCENT':
      params.push({ label: 'Percent', value: rule.discountPercent ?? '—' });
      break;
    case 'DISCOUNT_FIXED':
      params.push({ label: 'Amount', value: rule.discountFixed ?? '—' });
      break;
    case 'FIRST_N_PRICE':
      params.push(
        { label: 'First', value: rule.firstN?.toString() ?? '—' },
        { label: 'at', value: rule.priceOverride ?? '—' },
      );
      break;
    case 'FIRST_N_DISCOUNT_PERCENT':
      params.push(
        { label: 'First', value: rule.firstN?.toString() ?? '—' },
        { label: 'at', value: `${rule.discountPercent ?? '—'}%` },
      );
      break;
    case 'FIRST_N_DISCOUNT_FIXED':
      params.push(
        { label: 'First', value: rule.firstN?.toString() ?? '—' },
        { label: 'minus', value: rule.discountFixed ?? '—' },
      );
      break;
  }

  return params;
}
